import type { ZodType } from 'zod'
import type { AuthQuotaMetric } from '@/features/auth/types'
import { assistantToolSchemas } from '@/features/assistant/tool-schemas'

export type AssistantToolTransport = 'mcp-ready' | 'browser-only'
export type AssistantToolKind = 'query' | 'mutation' | 'job'

export interface AssistantToolCommercialPolicy {
  requiresAuth?: boolean
  requiredAppFeatures?: string[]
  requiredAiEntitlements?: string[]
  requiredAutomationEntitlements?: string[]
  quotaMetric?: AuthQuotaMetric | null
  billable?: boolean
}

export interface AssistantToolCatalogDefinition<TInput = unknown, TOutput = unknown> {
  name: string
  description: string
  kind: AssistantToolKind
  transport: AssistantToolTransport
  preferredRequestTimeoutMs?: number
  preconditions?: string[]
  sideEffects?: string[]
  supportsProgress?: boolean
  supportsCancellation?: boolean
  commercialPolicy?: AssistantToolCommercialPolicy
  inputSchema: ZodType<TInput>
  outputSchema: ZodType<TOutput>
}

export type AssistantToolCatalogAnyDefinition = AssistantToolCatalogDefinition<any, any>

const assistantToolCatalogDefinitions: AssistantToolCatalogAnyDefinition[] = [
  {
    name: 'get_current_project',
    description: 'Read the currently opened project in the local VideoCut editor.',
    kind: 'query',
    transport: 'mcp-ready',
    preconditions: ['workspace-ready'],
    inputSchema: assistantToolSchemas.getCurrentProject.input,
    outputSchema: assistantToolSchemas.getCurrentProject.output,
  },
  {
    name: 'read_current_project_disk_snapshot',
    description:
      'Read the persisted project snapshot from workspace files, including project.json, media links, render queue, and exports.',
    kind: 'query',
    transport: 'mcp-ready',
    preconditions: ['workspace-ready', 'project-open'],
    inputSchema: assistantToolSchemas.readCurrentProjectDiskSnapshot.input,
    outputSchema: assistantToolSchemas.readCurrentProjectDiskSnapshot.output,
  },
  {
    name: 'analyze_current_project_structure',
    description:
      'Analyze the persisted project structure for MCP planning, including timeline layout, media linkage, exports, and structural warnings.',
    kind: 'query',
    transport: 'mcp-ready',
    preconditions: ['workspace-ready', 'project-open'],
    inputSchema: assistantToolSchemas.analyzeCurrentProjectStructure.input,
    outputSchema: assistantToolSchemas.analyzeCurrentProjectStructure.output,
  },
  {
    name: 'create_project',
    description: 'Create a new local project and switch the media library into that project.',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['workspace-ready'],
    sideEffects: ['creates-project', 'switches-current-project', 'refreshes-media-items'],
    inputSchema: assistantToolSchemas.createProject.input,
    outputSchema: assistantToolSchemas.createProject.output,
  },
  {
    name: 'open_project',
    description: 'Open an existing local project by id.',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['workspace-ready'],
    sideEffects: ['switches-current-project', 'refreshes-media-items'],
    inputSchema: assistantToolSchemas.openProject.input,
    outputSchema: assistantToolSchemas.openProject.output,
  },
  {
    name: 'list_project_media',
    description: 'List media currently attached to a project in the local media library.',
    kind: 'query',
    transport: 'mcp-ready',
    preconditions: ['workspace-ready'],
    inputSchema: assistantToolSchemas.listProjectMedia.input,
    outputSchema: assistantToolSchemas.listProjectMedia.output,
  },
  {
    name: 'analyze_project_media',
    description: 'Analyze project media inventory and timeline usage for planning workflows.',
    kind: 'query',
    transport: 'mcp-ready',
    preconditions: ['workspace-ready', 'project-open'],
    inputSchema: assistantToolSchemas.analyzeProjectMedia.input,
    outputSchema: assistantToolSchemas.analyzeProjectMedia.output,
  },
  {
    name: 'import_local_media',
    description: 'Import local files or a remote URL into the current project media library.',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['workspace-ready', 'project-open'],
    sideEffects: ['opens-file-picker-or-fetches-url', 'writes-media-to-project'],
    inputSchema: assistantToolSchemas.importLocalMedia.input,
    outputSchema: assistantToolSchemas.importLocalMedia.output,
  },
  {
    name: 'summarize_timeline',
    description: 'Summarize the active timeline state for the current project.',
    kind: 'query',
    transport: 'mcp-ready',
    preconditions: ['project-open'],
    inputSchema: assistantToolSchemas.summarizeTimeline.input,
    outputSchema: assistantToolSchemas.summarizeTimeline.output,
  },
  {
    name: 'draft_edit_plan',
    description: 'Generate a structured JIAN edit plan for the current prompt.',
    kind: 'job',
    transport: 'mcp-ready',
    preferredRequestTimeoutMs: 60_000,
    commercialPolicy: {
      requiresAuth: true,
      requiredAiEntitlements: ['edit_planning'],
      requiredAutomationEntitlements: ['mcp_access'],
      quotaMetric: 'ai_processing_seconds',
      billable: true,
    },
    inputSchema: assistantToolSchemas.draftEditPlan.input,
    outputSchema: assistantToolSchemas.draftEditPlan.output,
  },
  {
    name: 'execute_plan_step',
    description:
      'Execute a single step from an existing assistant plan and return the updated job state.',
    kind: 'job',
    transport: 'mcp-ready',
    preferredRequestTimeoutMs: 180_000,
    supportsProgress: true,
    supportsCancellation: true,
    inputSchema: assistantToolSchemas.executePlanStep.input,
    outputSchema: assistantToolSchemas.executePlanStep.output,
  },
  {
    name: 'generate_tts_audio',
    description: 'Generate speech audio inside the browser runtime using a supported TTS engine.',
    kind: 'job',
    transport: 'browser-only',
    preferredRequestTimeoutMs: 120_000,
    preconditions: ['runtime-tts-available'],
    sideEffects: ['calls-local-or-configured-tts-engine'],
    commercialPolicy: {
      requiresAuth: true,
      requiredAiEntitlements: ['tts_generation'],
      requiredAutomationEntitlements: ['mcp_access'],
      quotaMetric: 'ai_processing_seconds',
      billable: true,
    },
    inputSchema: assistantToolSchemas.generateTtsAudio.input,
    outputSchema: assistantToolSchemas.generateTtsAudio.output,
  },
  {
    name: 'save_generated_audio_to_media',
    description:
      'Persist generated audio into project media so it can be reused by timeline tools.',
    kind: 'mutation',
    transport: 'browser-only',
    preconditions: ['workspace-ready', 'project-open'],
    sideEffects: ['writes-generated-audio-to-project-media'],
    inputSchema: assistantToolSchemas.saveGeneratedAudioToMedia.input,
    outputSchema: assistantToolSchemas.saveGeneratedAudioToMedia.output,
  },
  {
    name: 'generate_bgm',
    description: 'Generate cloud background music for a short voiceover video.',
    kind: 'job',
    transport: 'browser-only',
    preferredRequestTimeoutMs: 120_000,
    preconditions: ['aliyun-music-service-configured'],
    sideEffects: ['calls-aliyun-music-service'],
    supportsProgress: true,
    supportsCancellation: true,
    inputSchema: assistantToolSchemas.generateBgm.input,
    outputSchema: assistantToolSchemas.generateBgm.output,
  },
  {
    name: 'insert_audio_to_timeline',
    description: 'Insert an audio media item into the current timeline.',
    kind: 'mutation',
    transport: 'browser-only',
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline'],
    inputSchema: assistantToolSchemas.insertAudioToTimeline.input,
    outputSchema: assistantToolSchemas.insertAudioToTimeline.output,
  },
  {
    name: 'attach_bgm_to_timeline',
    description: 'Attach looping background music to the current timeline.',
    kind: 'mutation',
    transport: 'browser-only',
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline'],
    inputSchema: assistantToolSchemas.attachBgmToTimeline.input,
    outputSchema: assistantToolSchemas.attachBgmToTimeline.output,
  },
  {
    name: 'transcribe_media',
    description: 'Generate a transcript for a media item.',
    kind: 'job',
    transport: 'mcp-ready',
    preferredRequestTimeoutMs: 180_000,
    preconditions: ['workspace-ready', 'project-open'],
    sideEffects: ['writes-transcript'],
    supportsProgress: true,
    supportsCancellation: true,
    commercialPolicy: {
      requiresAuth: true,
      requiredAiEntitlements: ['transcription'],
      requiredAutomationEntitlements: ['mcp_access'],
      quotaMetric: 'ai_processing_seconds',
      billable: true,
    },
    inputSchema: assistantToolSchemas.transcribeMedia.input,
    outputSchema: assistantToolSchemas.transcribeMedia.output,
  },
  {
    name: 'insert_transcript_as_captions',
    description: 'Turn an existing transcript into timeline captions.',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['project-open', 'transcript-available'],
    sideEffects: ['mutates-timeline'],
    inputSchema: assistantToolSchemas.insertTranscriptAsCaptions.input,
    outputSchema: assistantToolSchemas.insertTranscriptAsCaptions.output,
  },
  {
    name: 'remove_filler_words',
    description: 'Analyze transcript-backed clips and remove filler-word ranges from the timeline.',
    kind: 'job',
    transport: 'mcp-ready',
    preferredRequestTimeoutMs: 120_000,
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline'],
    commercialPolicy: {
      requiresAuth: true,
      requiredAiEntitlements: ['ai_cleanup'],
      requiredAutomationEntitlements: ['mcp_access'],
      quotaMetric: 'ai_processing_seconds',
      billable: true,
    },
    inputSchema: assistantToolSchemas.removeFillerWords.input,
    outputSchema: assistantToolSchemas.removeFillerWords.output,
  },
  {
    name: 'remove_silence',
    description: 'Analyze clip waveforms and remove silence ranges from the timeline.',
    kind: 'job',
    transport: 'mcp-ready',
    preferredRequestTimeoutMs: 120_000,
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline'],
    commercialPolicy: {
      requiresAuth: true,
      requiredAiEntitlements: ['ai_cleanup'],
      requiredAutomationEntitlements: ['mcp_access'],
      quotaMetric: 'ai_processing_seconds',
      billable: true,
    },
    inputSchema: assistantToolSchemas.removeSilence.input,
    outputSchema: assistantToolSchemas.removeSilence.output,
  },
  {
    name: 'start_export',
    description: 'Run a browser-side export for the active project.',
    kind: 'job',
    transport: 'mcp-ready',
    preferredRequestTimeoutMs: 240_000,
    preconditions: ['workspace-ready', 'project-open', 'runtime-export-available'],
    sideEffects: ['renders-project', 'optionally-triggers-download'],
    supportsProgress: true,
    supportsCancellation: true,
    inputSchema: assistantToolSchemas.startExport.input,
    outputSchema: assistantToolSchemas.startExport.output,
  },
  {
    name: 'get_runtime_capabilities',
    description: 'Report browser runtime capabilities relevant to local-first assistant tools.',
    kind: 'query',
    transport: 'mcp-ready',
    inputSchema: assistantToolSchemas.getRuntimeCapabilities.input,
    outputSchema: assistantToolSchemas.getRuntimeCapabilities.output,
  },
  {
    name: 'replace_caption_style',
    description: '批量修改已有字幕的视觉样式，包括字体、颜色、背景、对齐、阴影、描边等。',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline'],
    inputSchema: assistantToolSchemas.replaceCaptionStyle.input,
    outputSchema: assistantToolSchemas.replaceCaptionStyle.output,
  },
  {
    name: 'add_brand_watermark',
    description: '在视频上添加品牌文字水印或品牌图片水印。',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline', 'creates-track-when-needed'],
    inputSchema: assistantToolSchemas.addBrandWatermark.input,
    outputSchema: assistantToolSchemas.addBrandWatermark.output,
  },
  {
    name: 'create_track',
    description: '在时间线上显式创建视频或音频轨道。',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['project-open'],
    sideEffects: ['creates-track'],
    inputSchema: assistantToolSchemas.createTrack.input,
    outputSchema: assistantToolSchemas.createTrack.output,
  },
  {
    name: 'insert_text_overlay',
    description: '在时间线上添加文本覆盖层，用于片头标题、角标、尾屏文案等。',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline', 'creates-track-when-needed'],
    inputSchema: assistantToolSchemas.insertTextOverlay.input,
    outputSchema: assistantToolSchemas.insertTextOverlay.output,
  },
  {
    name: 'insert_image_overlay',
    description: '在时间线上添加图片覆盖层，用于 Logo、角标、贴图、水印等。',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline', 'creates-track-when-needed'],
    inputSchema: assistantToolSchemas.insertImageOverlay.input,
    outputSchema: assistantToolSchemas.insertImageOverlay.output,
  },
  {
    name: 'set_item_transform',
    description: '修改时间线上某个元素的位置、大小、旋转、透明度。',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline'],
    inputSchema: assistantToolSchemas.setItemTransform.input,
    outputSchema: assistantToolSchemas.setItemTransform.output,
  },
  {
    name: 'add_item_keyframes',
    description: '为时间线上的元素添加关键帧动画。',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline', 'adds-keyframes'],
    inputSchema: assistantToolSchemas.addItemKeyframes.input,
    outputSchema: assistantToolSchemas.addItemKeyframes.output,
  },
  {
    name: 'apply_transition',
    description: '在两个相邻片段之间添加转场效果。',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline', 'adds-transition'],
    inputSchema: assistantToolSchemas.applyTransition.input,
    outputSchema: assistantToolSchemas.applyTransition.output,
  },
  {
    name: 'apply_item_effect_preset',
    description: '为视频或图片元素应用滤镜/特效预设。',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline', 'adds-effects'],
    inputSchema: assistantToolSchemas.applyItemEffectPreset.input,
    outputSchema: assistantToolSchemas.applyItemEffectPreset.output,
  },
  {
    name: 'create_mask_for_item',
    description: '为元素添加基础 shape mask（矩形/圆形/椭圆/路径）。',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline'],
    inputSchema: assistantToolSchemas.createMaskForItem.input,
    outputSchema: assistantToolSchemas.createMaskForItem.output,
  },
  {
    name: 'trim_clip',
    description: '修剪片段起点或终点，可选 ripple 模式。',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline'],
    inputSchema: assistantToolSchemas.trimClip.input,
    outputSchema: assistantToolSchemas.trimClip.output,
  },
  {
    name: 'split_clip',
    description:
      'Split a clip at a specific frame, creating two adjacent clips. Optionally ripple the gap closed.',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline'],
    inputSchema: assistantToolSchemas.splitClip.input,
    outputSchema: assistantToolSchemas.splitClip.output,
  },
  {
    name: 'set_audio_volume',
    description: 'Set the volume in dB for a single audio or video item.',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline'],
    inputSchema: assistantToolSchemas.setAudioVolume.input,
    outputSchema: assistantToolSchemas.setAudioVolume.output,
  },
  {
    name: 'set_audio_fade',
    description: 'Set fade-in and/or fade-out duration in seconds for an audio item.',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline'],
    inputSchema: assistantToolSchemas.setAudioFade.input,
    outputSchema: assistantToolSchemas.setAudioFade.output,
  },
  {
    name: 'duplicate_item',
    description:
      'Duplicate a timeline item to the same or a different track, optionally at a specific position.',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline', 'creates-track-when-needed'],
    inputSchema: assistantToolSchemas.duplicateItem.input,
    outputSchema: assistantToolSchemas.duplicateItem.output,
  },
  {
    name: 'get_plan_status',
    description:
      'Get the current status of the assistant plan and job execution from the local job store.',
    kind: 'query',
    transport: 'mcp-ready',
    preconditions: [],
    inputSchema: assistantToolSchemas.getPlanStatus.input,
    outputSchema: assistantToolSchemas.getPlanStatus.output,
  },
  {
    name: 'confirm_plan',
    description: 'Confirm the current draft plan and transition it into a running job.',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['workspace-ready'],
    sideEffects: [],
    inputSchema: assistantToolSchemas.confirmPlan.input,
    outputSchema: assistantToolSchemas.confirmPlan.output,
  },
  {
    name: 'ripple_delete_range',
    description:
      'Delete timeline items by ID, track+range, or all items in a frame range with ripple shift.',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline'],
    inputSchema: assistantToolSchemas.rippleDeleteRange.input,
    outputSchema: assistantToolSchemas.rippleDeleteRange.output,
  },
  {
    name: 'freeze_frame_at',
    description:
      'Insert a freeze-frame still image at a specific frame, splitting the video clip and shifting downstream items.',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline', 'calls-media-operations'],
    preferredRequestTimeoutMs: 30_000,
    inputSchema: assistantToolSchemas.freezeFrameAt.input,
    outputSchema: assistantToolSchemas.freezeFrameAt.output,
  },
  {
    name: 'set_in_out_points',
    description: 'Set or clear the timeline in/out points for targeted export.',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['project-open'],
    sideEffects: [],
    inputSchema: assistantToolSchemas.setInOutPoints.input,
    outputSchema: assistantToolSchemas.setInOutPoints.output,
  },
  {
    name: 'update_caption_items',
    description:
      'Update text, timing, or duration of caption items attached to a specific timeline clip.',
    kind: 'mutation',
    transport: 'mcp-ready',
    preconditions: ['project-open'],
    sideEffects: ['mutates-timeline'],
    inputSchema: assistantToolSchemas.updateCaptionItems.input,
    outputSchema: assistantToolSchemas.updateCaptionItems.output,
  },
]

const assistantToolCatalogMap = assistantToolCatalogDefinitions.reduce<
  Record<string, AssistantToolCatalogAnyDefinition>
>((map, definition) => {
  map[definition.name] = definition
  return map
}, {})

export function getAssistantToolCatalogDefinitions(): AssistantToolCatalogAnyDefinition[] {
  return [...assistantToolCatalogDefinitions]
}

export function getAssistantToolCatalogDefinition(
  name: string,
): AssistantToolCatalogAnyDefinition | null {
  return assistantToolCatalogMap[name] ?? null
}
